// Generic client-side chunked upload, used to bypass nginx's 1MB
// client_max_body_size on prod. Splits a Blob into <1MB chunks, posts each
// to /api/chunked-upload/chunk, and returns the uploadId that downstream
// endpoints consume via consumeChunkedUpload(uploadId) on the server.

import axios from '@/axiosConfig';

const CHUNK_SIZE = 800 * 1024; // 800KB — safely under nginx 1MB default

export async function uploadChunked(blob, opts = {}) {
  if (!(blob instanceof Blob)) {
    throw new Error('uploadChunked requires a Blob');
  }
  const totalSize = blob.size;
  const totalChunks = Math.max(1, Math.ceil(totalSize / CHUNK_SIZE));
  // Default above global 20s axios timeout — large PDFs / slow DEV networks
  const timeout = opts.timeout ?? 120000;

  const initRes = await axios.post(
    '/api/chunked-upload/init',
    {
      totalChunks,
      totalSize,
    },
    { timeout },
  );
  const uploadId = initRes.data && initRes.data.uploadId;
  if (!uploadId) throw new Error('chunked-upload/init returned no uploadId');

  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, totalSize);
    const slice = blob.slice(start, end);
    const fd = new FormData();
    fd.append('uploadId', uploadId);
    fd.append('chunkIndex', String(i));
    fd.append('chunk', slice, `chunk_${i}`);
    await axios.post('/api/chunked-upload/chunk', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout,
    });
    if (typeof opts.onProgress === 'function') {
      opts.onProgress({ sent: i + 1, total: totalChunks });
    }
  }

  return uploadId;
}

export default uploadChunked;
